import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseApp } from '@angular/fire/app';
import { Firestore, doc, getDoc, setDoc, updateDoc } from '@angular/fire/firestore';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import { ToastService } from '../../shared/ui/toast/toast.service';

export type WebPushPermission = NotificationPermission | 'unsupported';

const DEVICE_ID_KEY = 'hv_web_device_id';
const SOFT_ASK_DISMISSED_KEY = 'hv_push_soft_ask_dismissed_at';
const SOFT_ASK_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;
const SW_PATH = '/firebase-messaging-sw.js';
const SW_SCOPE = '/firebase-cloud-messaging-push-scope';

/**
 * Browser (FCM web) push for the admin app.
 *
 * Config: `environment.firebase.vapidKey` (Firebase console → Project settings → Cloud Messaging →
 * Web Push certificates). While empty, push is skipped gracefully and the UI hides the soft-ask.
 *
 * The service worker (`public/firebase-messaging-sw.js`) receives the Firebase config through its
 * registration URL query string, so one static file serves every environment.
 *
 * Permission UX: never prompts on load. `Notification.requestPermission()` is only called from
 * `enable()`, i.e. after the user clicks "Enable" in the bell soft-ask card or in Settings.
 */
@Injectable({ providedIn: 'root' })
export class WebPushService {
  private readonly app = inject(FirebaseApp);
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  private readonly vapidKey: string = (environment.firebase as { vapidKey?: string }).vapidKey ?? '';

  readonly supported = signal<boolean | null>(null);
  readonly permission = signal<WebPushPermission>(this.readPermission());
  readonly configured = !!this.vapidKey;
  readonly enabling = signal(false);
  readonly tokenRegistered = signal(false);
  private readonly softAskDismissedAt = signal<number>(this.readDismissedAt());

  /** Whether to show the tasteful "enable alerts" card at the top of the bell panel. */
  readonly showSoftAsk = computed(
    () =>
      this.supported() === true &&
      this.configured &&
      this.permission() === 'default' &&
      Date.now() - this.softAskDismissedAt() > SOFT_ASK_SNOOZE_MS
  );

  private initialized = false;
  private foregroundAttached = false;
  private lastSyncedUid: string | null = null;

  constructor() {
    // Re-sync the device token when the signed-in user changes (permission already granted).
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      untracked(() => {
        if (!uid) {
          this.lastSyncedUid = null;
          this.tokenRegistered.set(false);
          return;
        }
        if (this.initialized && this.permission() === 'granted' && this.lastSyncedUid !== uid) {
          void this.registerAndSync();
        }
      });
    });
  }

  /** Idempotent startup hook (called by the notification bell). Never prompts. */
  async init(): Promise<void> {
    if (this.initialized) return;
    this.initialized = true;

    const supported = await this.checkSupported();
    this.supported.set(supported);
    if (!supported) {
      this.permission.set('unsupported');
      return;
    }
    this.permission.set(Notification.permission);
    this.listenForSwClicks();

    if (!this.configured) {
      console.info('[web-push] environment.firebase.vapidKey is empty — browser push disabled.');
      return;
    }
    if (Notification.permission === 'granted' && this.auth.user()) {
      await this.registerAndSync();
    }
  }

  /** Ask for permission (user gesture) and register this browser for push. */
  async enable(): Promise<boolean> {
    if (!this.initialized) await this.init();
    if (!this.supported()) {
      this.toast.warning('This browser does not support push notifications.');
      return false;
    }
    if (!this.configured) {
      console.info('[web-push] vapidKey missing — cannot enable browser push in this environment.');
      this.toast.info('Browser notifications are not configured for this environment yet.');
      return false;
    }
    this.enabling.set(true);
    try {
      const result = await Notification.requestPermission();
      this.permission.set(result);
      if (result !== 'granted') {
        if (result === 'denied') {
          this.toast.warning('Notifications are blocked. You can allow them from your browser’s site settings.');
        }
        return false;
      }
      const ok = await this.registerAndSync();
      if (ok) this.toast.success('Browser notifications enabled');
      return ok;
    } finally {
      this.enabling.set(false);
    }
  }

  /** Hide the soft-ask card for 14 days. */
  dismissSoftAsk(): void {
    const now = Date.now();
    try {
      localStorage.setItem(SOFT_ASK_DISMISSED_KEY, String(now));
    } catch {
      /* storage unavailable */
    }
    this.softAskDismissedAt.set(now);
  }

  // ---------------------------------------------------------------------------------------

  private async registerAndSync(): Promise<boolean> {
    const uid = this.auth.user()?.uid;
    if (!uid || !this.configured) return false;
    try {
      const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
      const registration = await this.registerServiceWorker();
      const messaging = getMessaging(this.app);
      const token = await getToken(messaging, {
        vapidKey: this.vapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return false;

      await this.saveDevice(uid, token);
      this.lastSyncedUid = uid;
      this.tokenRegistered.set(true);

      if (!this.foregroundAttached) {
        this.foregroundAttached = true;
        onMessage(messaging, (payload) => this.onForegroundMessage(payload));
      }
      return true;
    } catch (err) {
      console.warn('[web-push] registration failed', err);
      return false;
    }
  }

  private async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const { vapidKey: _omit, ...cfg } = environment.firebase as Record<string, string | undefined>;
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(cfg)) {
      if (v) params.set(k, String(v));
    }
    return navigator.serviceWorker.register(`${SW_PATH}?${params.toString()}`, { scope: SW_SCOPE });
  }

  private async saveDevice(uid: string, fcmToken: string): Promise<void> {
    const deviceId = this.deviceId();
    const ref = doc(this.firestore, `users/${uid}/devices/${deviceId}`);
    const now = Date.now();
    const snap = await getDoc(ref);
    if (snap.exists()) {
      await updateDoc(ref, {
        fcmToken,
        enabled: true,
        userAgent: navigator.userAgent,
        lastSeenAt: now,
      });
    } else {
      await setDoc(ref, {
        platform: 'web',
        fcmToken,
        userAgent: navigator.userAgent,
        enabled: true,
        createdAt: now,
        lastSeenAt: now,
      });
    }
  }

  private onForegroundMessage(payload: {
    notification?: { title?: string; body?: string };
    data?: Record<string, string>;
  }): void {
    const data = payload.data ?? {};
    const title = payload.notification?.title || data['title'] || 'Haulverse';
    const body = payload.notification?.body || data['body'] || '';
    const severity = data['severity'] || 'info';
    if (severity === 'critical') {
      this.beep();
      this.toast.error(body || title, { title, duration: 12000, showClose: true });
    } else if (severity === 'action') {
      this.toast.warning(body || title, { title, showClose: true });
    } else {
      this.toast.info(body || title, { title });
    }
  }

  /** Short two-tone alert for critical events (no audio asset needed). */
  private beep(): void {
    try {
      const Ctx: typeof AudioContext | undefined =
        window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const tone = (freq: number, start: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + dur + 0.05);
      };
      tone(880, 0, 0.18);
      tone(660, 0.22, 0.24);
      setTimeout(() => void ctx.close(), 800);
    } catch {
      /* autoplay restrictions etc. */
    }
  }

  private listenForSwClicks(): void {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const msg = event.data as { type?: string; deepLink?: string } | null;
      if (msg?.type === 'hv-notification-click' && msg.deepLink) {
        void this.router.navigateByUrl(msg.deepLink);
      }
    });
    navigator.serviceWorker.startMessages?.();
  }

  private async checkSupported(): Promise<boolean> {
    if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
      return false;
    }
    try {
      const { isSupported } = await import('firebase/messaging');
      return await isSupported();
    } catch {
      return false;
    }
  }

  private deviceId(): string {
    let id: string | null = null;
    try {
      id = localStorage.getItem(DEVICE_ID_KEY);
    } catch {
      /* ignore */
    }
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? `web_${crypto.randomUUID().replace(/-/g, '')}`
          : `web_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      try {
        localStorage.setItem(DEVICE_ID_KEY, id);
      } catch {
        /* ignore */
      }
    }
    return id;
  }

  private readPermission(): WebPushPermission {
    if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
    return Notification.permission;
  }

  private readDismissedAt(): number {
    try {
      return Number(localStorage.getItem(SOFT_ASK_DISMISSED_KEY) || 0) || 0;
    } catch {
      return 0;
    }
  }
}
